import React, { useContext } from "react";
import { useFragment } from "react-relay";

import { faPlus, faStarShooting } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { beforeWrite, type ModifierArguments, type Options } from "@popperjs/core";
import { store as emojiStore } from "emoji-mart";
import { EventEmitter } from "events";
import { motion } from "framer-motion";
import { throttle } from "lodash";
import maxSize from "popper-max-size-modifier";
import { graphql } from "relay-runtime";
import uuidv4 from "uuid/v4";

import growthbook from "~/lib/ab/growthbook";
import { useMedia } from "~/lib/hooks";
import { useDebouncedResizeObserver } from "~/lib/hooks/useResizeObserver";
import { isMobile } from "~/lib/utils";
import { useGroupEmitter } from "~/lib/websocket";
import { Button, Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { Emoji, Picker as EmojiPicker } from "../common/Emoji";
import ViewportContext from "../common/ViewportContext";
import { ScrollContext } from "../layout/ScrollContext";
import { LazyTippy } from "./CreateEditGroupModal";
import type { NoteSocketPayload } from "./NoteCard";
import { type AbstractAction, ActionButton } from "./SuggestedActions/shared";

import type { LiveReactions_viewer$key } from "~/__generated__/LiveReactions_viewer.graphql";

import styles from "./css/LiveReactions.module.css";

const ACTION_ID = "live-reactions" as const;
export const LIVE_REACTIONS_FEATURE_FLAG_ID = "live-reactions";

const EMOJI_SIZE = 50;
const MAX_VISIBLE_EMOJIS = 20;

const viewerFragment = graphql`
	fragment LiveReactions_viewer on Viewer {
		account {
			id
		}
		settings {
			enableLiveReactions
		}
	}
`;

function getInitialScale(scaleFactor: number, scaleDir: number) {
	return 0.8 + scaleFactor - scaleDir;
}

function getAnimationScale(scaleFactor: number, scaleDir: number) {
	return 0.7 + scaleFactor + scaleDir;
}

function getRandomXPositionForNote(
	noteContainerWidth: number,
	dir: number,
	xWobble: number,
	scaleFactor: number,
	scaleDir: number,
) {
	// return a random x position with a 50% margin on left, and 5% margin on right
	let xPos = Math.floor(noteContainerWidth * 0.5 + Math.random() * (noteContainerWidth * 0.45 - EMOJI_SIZE));

	// check whether the emoji will overflow the note container, and adjust the x position accordingly
	// dir is 1 or -1, indicating right or left
	// when animated, the emoji will move to a maximum distance of xWobble on either side
	let movementDistance = Math.abs(dir * xWobble);
	const maxSize = Math.max(
		getInitialScale(scaleFactor, scaleDir) * EMOJI_SIZE,
		getAnimationScale(scaleFactor, scaleDir) * EMOJI_SIZE,
		EMOJI_SIZE,
	);
	const maxXDistance = xPos + maxSize + movementDistance;
	const overflow = maxXDistance - noteContainerWidth;
	if (overflow > 0) {
		xPos = xPos - overflow - maxSize;
		// we don't have to subtract maxSize to avoid overflow, but it's nice to not have a bunch of emojis crowding the right side
	}
	return xPos;
}

function getDurationAndForNote(height: number) {
	// Calculate the duration based on height and 300px/second (max 10s, due to framer spring limit)
	const duration = Math.max(Math.min(10, height / 300), 2);
	return duration;
}

function getRemainingDuration(emoji: { launchTime: number; duration: number }) {
	// When an emoji is unmounted, we need to know how long to make the exit y-translate animation so that it's seamless
	// and the emoji reaches the top of the note at the same speed it was going before.
	const timeAlreadyOnScreen = Date.now() - emoji.launchTime;
	const remainingDuration = emoji.duration - timeAlreadyOnScreen / 1000;
	return remainingDuration;
}

const eventEmitter = new EventEmitter();

export function LiveReactionContainer({
	noteGroupName,
	viewerKey,
	noteContainerRef,
}: {
	noteGroupName: string;
	viewerKey: LiveReactions_viewer$key;
	noteContainerRef: React.RefObject<HTMLDivElement>;
}) {
	const viewer = useFragment(viewerFragment, viewerKey);

	const [_updateCount, forceUpdate] = React.useReducer(x => x + 1, 0);
	const visibleEmojis = React.useRef<
		{
			id: string;
			name: string;
			duration: number;
			launchTime: number;
			dir: number;
			scaleDir: number;
			scaleFactor: number;
			xPos: number;
			xWobble: number;
			delay: number;
			sender: string;
		}[]
	>([]);
	const scrollContainer = useContext(ScrollContext);

	// Some sizing computation, using the available space in the scroll container and the note height, whichever is smaller
	const { height: scrollContainerHeight } = useDebouncedResizeObserver({ wait: 500, ref: scrollContainer });
	const { width: noteWidth, height: noteHeight } = useDebouncedResizeObserver({ wait: 500, ref: noteContainerRef });
	const noteContainerWidth: number = noteWidth || window.innerWidth;
	const noteContainerHeight: number =
		Math.min(noteHeight || window.innerHeight, scrollContainerHeight || window.innerHeight) - 120;

	const socket = useGroupEmitter(noteGroupName);

	const cleanupEmojis = React.useCallback(() => {
		if (!viewer.settings.enableLiveReactions || !growthbook.isOn(LIVE_REACTIONS_FEATURE_FLAG_ID)) {
			visibleEmojis.current = [];
			return;
		}
		const emojisToUnmount = visibleEmojis.current
			.filter(emoji => getRemainingDuration(emoji) < 0)
			.map(emoji => emoji.id);
		if (emojisToUnmount.length > 0) {
			visibleEmojis.current = visibleEmojis.current.filter(emoji => !emojisToUnmount.includes(emoji.id));
			forceUpdate();
		}
	}, [viewer.settings.enableLiveReactions]);
	React.useEffect(() => {
		// Unmount emojis that have been on screen for longer than their duration.
		// This is a symptom of this component not being very React-y.
		// The emojis state is only updated when a new batch of emojis arrives to replace it,
		// so we need a way to clean up the last batch of emojis rendered.
		cleanupEmojis();
		const timeout = setTimeout(cleanupEmojis, 4000);
		return () => {
			clearTimeout(timeout);
		};
	}, [cleanupEmojis]);

	const queueEmojis = React.useCallback(
		(emojiList: string[], sender: string, immediate = false) => {
			cleanupEmojis();
			if (!viewer.settings.enableLiveReactions || !growthbook.isOn(LIVE_REACTIONS_FEATURE_FLAG_ID)) return;
			// Drop some of the new emojis if there are too many (and they're not the immediate local emojis)
			const numEmojisToDrop = immediate ? 0 : visibleEmojis.current.length + emojiList.length - MAX_VISIBLE_EMOJIS;

			visibleEmojis.current.push(
				...emojiList.slice(numEmojisToDrop).map(emoji => {
					const dir = Math.random() > 0.5 ? 1 : -1;
					const scaleDir = (Math.random() > 0.5 ? 1 : -1) * 0.05; // Changes scale when moving
					const scaleFactor = Math.random() * 0.2; // Initial scale
					const xWobble = Math.min(noteContainerWidth, noteContainerHeight / 2) * 0.04 * (1 - scaleFactor * 2);
					const duration = getDurationAndForNote(noteContainerHeight);
					const xPos = noteContainerWidth
						? getRandomXPositionForNote(noteContainerWidth, dir, xWobble, scaleFactor, scaleDir)
						: 0;
					return {
						id: uuidv4(),
						name: emoji,
						duration: duration,
						launchTime: Date.now(),
						dir: dir,
						scaleDir: scaleDir,
						scaleFactor: scaleFactor,
						xPos: xPos,
						xWobble: xWobble,
						delay: immediate ? 0 : Math.random() * 0.45,
						sender: sender,
					};
				}),
			);
			forceUpdate();
		},
		[cleanupEmojis, noteContainerHeight, noteContainerWidth, viewer.settings.enableLiveReactions],
	);

	React.useEffect(() => {
		const onReactionSent = ({ group, emojis }: { group: string; emojis: string[] }) => {
			if (group !== noteGroupName) return;
			queueEmojis(emojis, "", true);
		};
		eventEmitter.addListener("LiveReactions.reactionSent", onReactionSent);
		const onSocketMessage = (payload: NoteSocketPayload) => {
			// We only want to render the emojis if Fellow is visible.
			// Otherwise the rendering will be delayed until Fellow becomes visible again
			if (payload.type === "LIVE_REACTION" && document.visibilityState === "visible") {
				// Add the new emojis to the state. They'll get removed elsewhere.
				if (!payload.data?.emojis?.length) return;
				if (payload.data.senderId === window.CLIENT_SESSION_ID) return;
				queueEmojis(payload.data.emojis, payload.data.sender);
			}
		};

		socket.on("message", onSocketMessage);
		return function () {
			socket.off("message", onSocketMessage);
			eventEmitter.removeListener("LiveReactions.reactionSent", onReactionSent);
		};
	}, [socket, queueEmojis, noteContainerHeight, noteContainerWidth, noteGroupName]);

	if (!viewer.settings.enableLiveReactions || !growthbook.isOn(LIVE_REACTIONS_FEATURE_FLAG_ID)) return <></>;
	return (
		<div className={styles.rainContainer}>
			{visibleEmojis.current.map(emoji => {
				const initialScale = getInitialScale(emoji.scaleFactor, emoji.scaleDir);
				const animationScale = getAnimationScale(emoji.scaleFactor, emoji.scaleDir);

				return (
					<motion.div
						initial={{ bottom: 0, x: emoji.xPos, scale: initialScale, opacity: 0 }}
						style={{
							display: "inline-block",
							position: "absolute",
							pointerEvents: "none",
							userSelect: "none",
							textAlign: "center",
						}}
						key={emoji.id}
						animate={{
							bottom: noteContainerHeight ?? window.innerHeight,
							scale: animationScale,
							marginLeft: [
								0,
								-1 * emoji.dir * emoji.xWobble,
								emoji.dir * emoji.xWobble,
								-1 * emoji.dir * emoji.xWobble,
								0,
							],
							opacity: [0, 1, 1, 0.8, 0],
							transition: {
								duration: emoji.duration,
								delay: emoji.delay,
								ease: [0.45, 0, 0.55, 1], // ease-in-out-quad
								times: [0, 0.25, 0.5, 0.75, 1],
							},
						}}
						transition={{ duration: emoji.duration, delay: emoji.delay }}
					>
						<Emoji accountId={viewer.account.id} size={EMOJI_SIZE} native emoji={emoji.name} />
						<br />
						{emoji.sender && (
							<div className={styles.emojiSender}>
								{" "}
								<span>{emoji.sender}</span>
							</div>
						)}
					</motion.div>
				);
			})}
		</div>
	);
}

export interface LiveReactionsProps {
	viewer: LiveReactions_viewer$key;
	noteId: string;
	noteGroupName: string;
}

export function LiveReactionsButton({ viewer, noteGroupName }: LiveReactionsProps) {
	const [emojiPickerOpen, setEmojiPickerOpen] = React.useState(false);
	const pendingEmojis = React.useRef<string[]>([]);

	const rainViewer = useFragment(viewerFragment, viewer);

	const showEmojiPicker = React.useCallback(() => setEmojiPickerOpen(true), []);
	const hideEmojiPicker = React.useCallback(() => setEmojiPickerOpen(false), []);

	const popperModifiers = {
		modifiers: [
			maxSize,
			{
				name: "applyMaxSize",
				enabled: true,
				phase: beforeWrite,
				requires: ["maxSize"],
				fn({ state }: ModifierArguments<Options>) {
					const { width, height } = state.modifiersData.maxSize;

					state.styles.popper = {
						...state.styles.popper,
						maxWidth: `${width}px`,
						maxHeight: `${height}px`,
					};
				},
			},
		],
	};

	const socket = useGroupEmitter(noteGroupName);

	const sendReactionsOverSocket = React.useMemo(
		() =>
			throttle(() => {
				if (pendingEmojis.current.length === 0) return;
				const emojis = pendingEmojis.current;
				pendingEmojis.current = [];
				socket.send({
					type: "live_reaction",
					data: {
						emojis,
					},
				});
			}, 250),
		[socket],
	);

	const { isMobile: isSmallScreen } = useContext(ViewportContext);
	const selectEmoji = React.useCallback(
		emoji => {
			const emojiId = emoji.colons || emoji.id;
			eventEmitter.emit("LiveReactions.reactionSent", { group: noteGroupName, emojis: [emojiId] });
			pendingEmojis.current = [...pendingEmojis.current, emojiId];
			sendReactionsOverSocket();

			// On small screens or mobile, make sure the emoji picker closes after selecting an emoji
			// This is because there's not enough space to see that the emoji sent in the background
			if (isSmallScreen || isMobile) hideEmojiPicker();
		},
		[noteGroupName, sendReactionsOverSocket, isSmallScreen, hideEmojiPicker],
	);
	const supportsHover = useMedia("(hover: hover)");

	const emojiSkin = emojiStore.get("skin") || 1;
	const defaultEmojis = [
		{ id: "+1", skin: emojiSkin, title: "Thumbs up" },
		{ id: "two_hearts", title: "Hearts" },
		{ id: "tada", title: "Party" },
		{ id: "clap", skin: emojiSkin, title: "Clap" },
		{ id: "alarm_clock", title: "Time check" },
		{ id: "cry", title: "Cry" },
		{ id: "-1", skin: emojiSkin, title: "Thumbs down" },
	];

	const selectDefaultEmoji = React.useCallback(
		e => {
			// This is pretty gross but if the <Emoji /> component is clicked, there's no way of getting the emoji string
			// so we have to set it on the button
			const emojiString = e.target.type === "button" ? e.target.name : e.target.parentElement.parentElement.name;
			selectEmoji({ id: emojiString });
		},
		[selectEmoji],
	);

	const renderEmojiPicker = React.useCallback(() => {
		return (
			<EmojiPicker
				autoFocus
				native
				emojiTooltip
				showPreview={supportsHover}
				emoji=""
				accountId={rainViewer.account.id}
				style={{ maxWidth: "95vw" }}
				onSelect={selectEmoji}
				perLine={7}
				skinEmoji="+1"
				title=""
				darkMode={document.documentElement.dataset.theme === "theme-dark"}
			/>
		);
	}, [rainViewer.account.id, selectEmoji, supportsHover]);

	return rainViewer.settings.enableLiveReactions && growthbook.isOn(LIVE_REACTIONS_FEATURE_FLAG_ID) ? (
		<Menu.Root>
			<Menu.Trigger>
				<div>
					<Tooltip content="Send a reaction" placement="bottom">
						<ActionButton actionId={ACTION_ID}>
							&#8203;{/* Zero-width space to make the line-height kick in since there's no text in here */}
							<FontAwesomeIcon icon={faStarShooting} transform="rotate-180" />
						</ActionButton>
					</Tooltip>
				</div>
			</Menu.Trigger>
			<Menu.Content className={styles.emojiMenu} side="top">
				{defaultEmojis.map(emoji => {
					const emojiString = `:${emoji.id}:${emoji.skin ? `:skin-tone-${emoji.skin}:` : ""}`;
					return (
						<Button
							className={styles.emojiButton}
							name={emojiString}
							key={emoji.id}
							title={emoji.title}
							onClick={selectDefaultEmoji}
							tabIndex={1}
						>
							<Emoji accountId={rainViewer.account.id} size={20} native emoji={emojiString} skin={emoji.skin} />
						</Button>
					);
				})}
				<LazyTippy
					visible={emojiPickerOpen}
					onClickOutside={hideEmojiPicker}
					onHide={hideEmojiPicker}
					interactive
					render={renderEmojiPicker}
					popperOptions={popperModifiers}
					appendTo={document.body}
				>
					<IconButton className={styles.emojiButton} title="All emojis" onClick={showEmojiPicker} icon={faPlus} />
				</LazyTippy>
			</Menu.Content>
		</Menu.Root>
	) : (
		<></>
	);
}

function MenuItem(props: LiveReactionsProps) {
	return (
		<Menu.Item>
			<LiveReactionsButton {...props} />
		</Menu.Item>
	);
}

export default {
	id: ACTION_ID,
	button: LiveReactionsButton,
	menuItem: MenuItem,
} satisfies AbstractAction;
